import React from 'react'
import { FacebookBrand, GoHighLevelBrand, LinkedInBrand } from 'blaze-ui/icons/35'
import {
  ExternalIntegrationEnum,
  ExternalIntegration,
  CROSS_POST_SUPPORTED_PROVIDERS,
} from 'api/clients/ExternalIntegrationsClient'
import { TwitterBrand, TikTokBrand, YouTubeBrand, Mailchimp, Wix, GoogleDrive, Shopify, Google } from 'blaze-ui/icons'
import {
  BrandPlanIntegrations,
  SelectedBlogPlatform,
  SelectedEmailPlatform,
  BrandPlanTactic,
  absurd,
  BrandPlan,
} from 'types'
import { getNormalizedTactics } from 'screens/Integrations/utils'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import {
  EMAIL_TACTICS,
  SEO_TACTICS,
  SOCIAL_MEDIA_TACTICS,
} from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import GoogleAnalytics from 'blaze-ui/icons/20/GoogleAnalytics'
import MetaBrand from 'blaze-ui/icons/20/MetaBrand'
import { InstagramReelsBrand } from 'blaze-ui/icons/30'
import { ContentType } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { InstagramBrand } from 'blaze-ui/icons/24'

export const TACTICS_WITH_GENERIC_INTEGRATIONS: BrandPlanIntegrations[] = ['seo', 'email']

export const tacticHasGenericIntegration = (tactic?: BrandPlanIntegrations): tactic is 'seo' | 'email' => {
  return !!(tactic && TACTICS_WITH_GENERIC_INTEGRATIONS.includes(tactic))
}

export interface TacticInfo {
  icon: React.ComponentType
  name: string
  integrationType: ExternalIntegrationEnum
  tactic?: BrandPlanIntegrations
}

export const IntegrationNameByProvider: Record<ExternalIntegrationEnum, string> = {
  [ExternalIntegrationEnum.Instagram]: 'Instagram',
  [ExternalIntegrationEnum.Facebook]: 'Facebook',
  [ExternalIntegrationEnum.LinkedIn]: 'LinkedIn',
  [ExternalIntegrationEnum.X]: 'X / Twitter',
  [ExternalIntegrationEnum.YouTube]: 'YouTube',
  [ExternalIntegrationEnum.TikTok]: 'TikTok',
  [ExternalIntegrationEnum.WordPress]: 'WordPress',
  [ExternalIntegrationEnum.Mailchimp]: 'Mailchimp',
  [ExternalIntegrationEnum.Google]: 'Google Analytics',
  [ExternalIntegrationEnum.GoogleDrive]: 'Google Drive',
  [ExternalIntegrationEnum.MetaAds]: 'Meta Ads',
  [ExternalIntegrationEnum.GoHighLevel]: 'GoHighLevel',
  [ExternalIntegrationEnum.Wix]: 'Wix',
  [ExternalIntegrationEnum.Shopify]: 'Shopify',
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'Google Business Profile',
  [ExternalIntegrationEnum.GoogleAds]: 'Google Ads',
}

export const CONTENT_TYPE_TO_TACTIC: Record<ContentType, BrandPlanIntegrations | null> = {
  instagram_post: 'instagram',
  instagram_story: 'instagram',
  instagram_reel: 'instagram_reel',
  tiktok_post: 'tiktok',
  facebook_post: 'facebook',
  facebook_story: 'facebook',
  linkedin_post: 'linkedin',
  twitter_post: 'twitter',
  twitter_thread: 'twitter',
  youtube_short: 'youtube',
  newsletter: 'email',
  blog_post: 'seo',
  crosspost_post: null,
  video_crosspost_post: null,
  google_my_business_post: 'google_my_business',
}

// Integration info constants
export const INTEGRATION_INFO = {
  SHOPIFY: {
    icon: Shopify,
    name: 'Shopify',
    integrationType: ExternalIntegrationEnum.Shopify,
    tactic: 'seo' as const,
  },
  INSTAGRAM: {
    icon: InstagramBrand,
    name: 'Instagram',
    integrationType: ExternalIntegrationEnum.Instagram,
    tactic: 'instagram' as const,
  },
  INSTAGRAM_REELS: {
    icon: InstagramReelsBrand,
    name: 'Instagram Reels',
    integrationType: ExternalIntegrationEnum.Instagram,
    tactic: 'instagram_reel' as const,
  },
  FACEBOOK: {
    icon: FacebookBrand,
    name: 'Facebook',
    integrationType: ExternalIntegrationEnum.Facebook,
    tactic: 'facebook' as const,
  },
  LINKEDIN: {
    icon: LinkedInBrand,
    name: 'LinkedIn',
    integrationType: ExternalIntegrationEnum.LinkedIn,
    tactic: 'linkedin' as const,
  },
  TWITTER: {
    icon: TwitterBrand,
    name: 'X/Twitter',
    integrationType: ExternalIntegrationEnum.X,
    tactic: 'twitter' as const,
  },
  TIKTOK: {
    icon: TikTokBrand,
    name: 'TikTok',
    integrationType: ExternalIntegrationEnum.TikTok,
    tactic: 'tiktok' as const,
  },
  YOUTUBE: {
    icon: YouTubeBrand,
    name: 'YouTube',
    integrationType: ExternalIntegrationEnum.YouTube,
    tactic: 'youtube' as const,
  },
  WORDPRESS: {
    icon: WordPressLogo,
    name: 'WordPress',
    integrationType: ExternalIntegrationEnum.WordPress,
    tactic: 'seo' as const,
  },
  GOHIGHLEVEL: {
    icon: GoHighLevelBrand,
    name: 'GoHighLevel',
    integrationType: ExternalIntegrationEnum.GoHighLevel,
    tactic: 'seo' as const,
  },
  WIX: {
    icon: Wix,
    name: 'Wix',
    integrationType: ExternalIntegrationEnum.Wix,
    tactic: 'seo' as const,
  },
  MAILCHIMP: {
    icon: Mailchimp,
    name: 'Mailchimp',
    integrationType: ExternalIntegrationEnum.Mailchimp,
    tactic: 'email' as const,
  },
  GOOGLE: {
    icon: GoogleAnalytics,
    name: 'Google Analytics',
    integrationType: ExternalIntegrationEnum.Google,
    tactic: 'google' as const,
  },
  GOOGLE_BUSINESS_PROFILE: {
    icon: Google,
    name: 'Google Business Profile',
    integrationType: ExternalIntegrationEnum.GoogleBusinessProfile,
    tactic: 'google_my_business' as const,
  },
  META_ADS: {
    icon: MetaBrand,
    name: 'Meta Ads',
    integrationType: ExternalIntegrationEnum.MetaAds,
  },
  GOOGLE_ADS: {
    icon: Google,
    name: 'Google Ads',
    integrationType: ExternalIntegrationEnum.GoogleAds,
  },
  GOOGLE_DRIVE: {
    icon: GoogleDrive,
    name: 'Google Drive',
    integrationType: ExternalIntegrationEnum.GoogleDrive,
  },
} as const

export const getIntegrationInfoByProvider = (integrationType: ExternalIntegrationEnum): TacticInfo | null => {
  const integrationInfo = Object.values(INTEGRATION_INFO).find((info) => info.integrationType === integrationType)
  return integrationInfo || null
}

export const getProviderLabel = (provider: ExternalIntegrationEnum): string => {
  return IntegrationNameByProvider[provider]
}

// Category-specific integration lists for AvailableIntegrations
export const AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS: TacticInfo[] = [
  INTEGRATION_INFO.TWITTER,
  INTEGRATION_INFO.YOUTUBE,
  INTEGRATION_INFO.TIKTOK,
  INTEGRATION_INFO.FACEBOOK,
  INTEGRATION_INFO.INSTAGRAM,
  INTEGRATION_INFO.LINKEDIN,
  INTEGRATION_INFO.GOOGLE_BUSINESS_PROFILE,
]

export const AVAILABLE_ADS_INTEGRATIONS: TacticInfo[] = [INTEGRATION_INFO.META_ADS, INTEGRATION_INFO.GOOGLE_ADS]

export const AVAILABLE_BLOG_PLATFORMS: TacticInfo[] = [
  INTEGRATION_INFO.WORDPRESS,
  INTEGRATION_INFO.GOHIGHLEVEL,
  INTEGRATION_INFO.WIX,
  INTEGRATION_INFO.SHOPIFY,
]

export const AVAILABLE_EMAIL_PLATFORMS: TacticInfo[] = [INTEGRATION_INFO.MAILCHIMP]

export const isBrandPlanIntegrationType = (provider: ExternalIntegrationEnum | undefined): boolean => {
  if (!provider) {
    return false
  }
  return (
    AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS.some((integration) => integration.integrationType === provider) ||
    AVAILABLE_BLOG_PLATFORMS.some((integration) => integration.integrationType === provider) ||
    AVAILABLE_EMAIL_PLATFORMS.some((integration) => integration.integrationType === provider)
  )
}

export const isSocialMediaIntegration = (provider: ExternalIntegrationEnum | undefined): boolean => {
  if (!provider) {
    return false
  }
  return AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS.some((integration) => integration.integrationType === provider)
}

export const isMultiAccountIntegration = (provider: ExternalIntegrationEnum | undefined): boolean => {
  return isSocialMediaIntegration(provider)
}

export const getIntegrationInfoFromProvider = (provider: ExternalIntegrationEnum): TacticInfo | undefined => {
  return Object.values(INTEGRATION_INFO).find((info) => info.integrationType === provider)
}

export const getTacticInfo = (
  tactic: BrandPlanIntegrations,
  selectedBlogPlatform?: SelectedBlogPlatform,
  selectedEmailPlatform?: SelectedEmailPlatform,
): TacticInfo | null => {
  switch (tactic) {
    case 'instagram':
      return INTEGRATION_INFO.INSTAGRAM
    case 'instagram_reel':
      return INTEGRATION_INFO.INSTAGRAM_REELS
    case 'facebook':
      return INTEGRATION_INFO.FACEBOOK
    case 'linkedin':
      return INTEGRATION_INFO.LINKEDIN
    case 'twitter':
      return INTEGRATION_INFO.TWITTER
    case 'tiktok':
      return INTEGRATION_INFO.TIKTOK
    case 'youtube':
      return INTEGRATION_INFO.YOUTUBE
    case 'seo':
      switch (selectedBlogPlatform) {
        case ExternalIntegrationEnum.WordPress:
          return INTEGRATION_INFO.WORDPRESS
        case ExternalIntegrationEnum.GoHighLevel:
          return INTEGRATION_INFO.GOHIGHLEVEL
        case ExternalIntegrationEnum.Wix:
          return INTEGRATION_INFO.WIX
        case ExternalIntegrationEnum.Shopify:
          return INTEGRATION_INFO.SHOPIFY
        default:
          return null
      }
    case 'email':
      switch (selectedEmailPlatform) {
        case ExternalIntegrationEnum.Mailchimp:
          return INTEGRATION_INFO.MAILCHIMP
        default:
          return null
      }
    case 'google':
      return INTEGRATION_INFO.GOOGLE
    case 'shopify':
      return INTEGRATION_INFO.SHOPIFY
    case 'meta_ads':
      return INTEGRATION_INFO.META_ADS
    case 'google_my_business':
      return INTEGRATION_INFO.GOOGLE_BUSINESS_PROFILE
    case 'google_ads' as BrandPlanIntegrations:
      return null
    default:
      absurd(tactic)
      return null
  }
}

/**
 * Gets the first integration index that hasn't been connected yet
 * @param tactics - Array of selected tactics
 * @param externalIntegrations - Array of connected external integrations
 * @param selectedBlogPlatform - Selected blog platform (required for SEO tactics)
 * @param selectedEmailPlatform - Selected email platform (required for email tactics)
 * @returns The index of the first unconnected integration, or null if all are connected
 */
export const getFirstUnconnectedIntegrationIndex = (
  tactics: BrandPlanIntegrations[],
  externalIntegrations: ExternalIntegration[],
  selectedBlogPlatform?: SelectedBlogPlatform,
  selectedEmailPlatform?: SelectedEmailPlatform,
): number | undefined => {
  const START_INDEX = 2

  // Get tactics organized by section (same logic as TacticIntegrationList)
  const socialMediaTactics = SOCIAL_MEDIA_TACTICS.filter((tactic) => tactics.includes(tactic))
  const seoTactics = SEO_TACTICS.filter((tactic) => tactics.includes(tactic))
  const emailTactics = EMAIL_TACTICS.filter((tactic) => tactics.includes(tactic))

  // Check Google integration (index 1)
  const googleTacticInfo = getTacticInfo('google')
  if (
    googleTacticInfo &&
    !externalIntegrations.some((integration) => integration.provider === googleTacticInfo.integrationType)
  ) {
    return 1
  }

  // Check social media tactics (starting at START_INDEX)
  for (let i = 0; i < socialMediaTactics.length; i++) {
    const tactic = socialMediaTactics[i]
    const tacticInfo = getTacticInfo(tactic)
    if (
      tacticInfo &&
      !externalIntegrations.some((integration) => integration.provider === tacticInfo.integrationType)
    ) {
      return START_INDEX + i
    }
  }

  // Check SEO tactics
  for (let i = 0; i < seoTactics.length; i++) {
    const tactic = seoTactics[i]
    const tacticInfo = getTacticInfo(tactic, selectedBlogPlatform, selectedEmailPlatform)

    // skip generic blogs since they don't have specific integrations so don't need to be connected
    if (!tacticInfo) {
      continue
    }

    if (
      tacticInfo &&
      !externalIntegrations.some((integration) => integration.provider === tacticInfo.integrationType)
    ) {
      return socialMediaTactics.length + START_INDEX + i
    }
  }

  // Check email tactics
  for (let i = 0; i < emailTactics.length; i++) {
    const tactic = emailTactics[i]
    const tacticInfo = getTacticInfo(tactic, selectedBlogPlatform, selectedEmailPlatform)

    // skip generic emails since they don't have specific integrations so don't need to be connected
    if (!tacticInfo) {
      continue
    }

    if (
      tacticInfo &&
      !externalIntegrations.some((integration) => integration.provider === tacticInfo.integrationType)
    ) {
      return socialMediaTactics.length + seoTactics.length + START_INDEX + i
    }
  }

  // All integrations are connected
  return undefined
}

export const integrationIsInBrandPlan = (
  integration: ExternalIntegration | undefined,
  brandPlan: BrandPlan | null | undefined,
): boolean => {
  if (!brandPlan || !integration) {
    return false
  }

  const normalizedTactics = getNormalizedTactics(brandPlan)
  const { tactics, blogPlatform, emailPlatform } = getProviderTacticsAndPlatforms(integration?.provider)

  // Check if any of the integration's associated tactics are in the brand plan
  const hasMatchingTactic = tactics.some((tactic) => normalizedTactics.includes(tactic as BrandPlanIntegrations))

  if (!hasMatchingTactic) {
    return false
  }

  if (blogPlatform !== undefined) {
    return brandPlan.selected_blog_platform === blogPlatform
  }

  if (emailPlatform !== undefined) {
    return brandPlan.selected_email_platform === emailPlatform
  }

  return true
}

/**
 * Gets the tactics and platform settings associated with an integration provider.
 * Returns the tactics that should be added/removed and the blog/email platform that should be set.
 */
export const getProviderTacticsAndPlatforms = (
  provider: ExternalIntegrationEnum,
): {
  tactics: BrandPlanTactic[]
  blogPlatform?: ExternalIntegrationEnum
  emailPlatform?: ExternalIntegrationEnum
} => {
  switch (provider) {
    case ExternalIntegrationEnum.Instagram:
      return {
        tactics: [BrandPlanTactic.Instagram, BrandPlanTactic.InstagramReel],
      }
    case ExternalIntegrationEnum.Facebook:
      return {
        tactics: [BrandPlanTactic.Facebook],
      }
    case ExternalIntegrationEnum.LinkedIn:
      return {
        tactics: [BrandPlanTactic.LinkedIn],
      }
    case ExternalIntegrationEnum.X:
      return {
        tactics: [BrandPlanTactic.Twitter],
      }
    case ExternalIntegrationEnum.TikTok:
      return {
        tactics: [BrandPlanTactic.TikTok],
      }
    case ExternalIntegrationEnum.YouTube:
      return {
        tactics: [BrandPlanTactic.YouTube],
      }
    case ExternalIntegrationEnum.WordPress:
      return {
        tactics: [BrandPlanTactic.SEO],
        blogPlatform: ExternalIntegrationEnum.WordPress,
      }
    case ExternalIntegrationEnum.GoHighLevel:
      return {
        tactics: [BrandPlanTactic.SEO],
        blogPlatform: ExternalIntegrationEnum.GoHighLevel,
      }
    case ExternalIntegrationEnum.Wix:
      return {
        tactics: [BrandPlanTactic.SEO],
        blogPlatform: ExternalIntegrationEnum.Wix,
      }
    case ExternalIntegrationEnum.Shopify:
      return {
        tactics: [BrandPlanTactic.SEO],
        blogPlatform: ExternalIntegrationEnum.Shopify,
      }
    case ExternalIntegrationEnum.Mailchimp:
      return {
        tactics: [BrandPlanTactic.Email],
        emailPlatform: ExternalIntegrationEnum.Mailchimp,
      }
    case ExternalIntegrationEnum.GoogleBusinessProfile:
      return {
        tactics: [BrandPlanTactic.GoogleBusinessProfile],
      }
    case ExternalIntegrationEnum.Google:
    case ExternalIntegrationEnum.GoogleDrive:
    case ExternalIntegrationEnum.MetaAds:
    case ExternalIntegrationEnum.GoogleAds:
      return {
        tactics: [],
      }

    default:
      absurd(provider)

      return {
        tactics: [],
      }
  }
}

/**
 * Gets the tactics to remove and platform fields to clear based on the integration provider
 */
export const getTacticsToRemove = (
  provider: ExternalIntegrationEnum,
): {
  tactics: BrandPlanTactic[]
  clearBlogPlatform?: boolean
  clearEmailPlatform?: boolean
} => {
  const { tactics, blogPlatform, emailPlatform } = getProviderTacticsAndPlatforms(provider)

  return {
    tactics,
    clearBlogPlatform: blogPlatform !== undefined,
    clearEmailPlatform: emailPlatform !== undefined,
  }
}

/**
 * Gets the tactics to add and platform fields to set based on the integration provider
 */
export const getTacticsToAdd = (
  provider: ExternalIntegrationEnum,
): {
  tactics: BrandPlanTactic[]
  setBlogPlatform?: ExternalIntegrationEnum
  setEmailPlatform?: ExternalIntegrationEnum
} => {
  const { tactics, blogPlatform, emailPlatform } = getProviderTacticsAndPlatforms(provider)

  return {
    tactics,
    setBlogPlatform: blogPlatform,
    setEmailPlatform: emailPlatform,
  }
}

export const shouldIntegrationCrosspost = (
  brandPlan: BrandPlan | undefined,
  provider: ExternalIntegrationEnum,
): boolean => {
  return brandPlan?.crosspost_frequency !== 'never' && CROSS_POST_SUPPORTED_PROVIDERS.includes(provider)
}

export const isLastAccountForProvider = (
  integration: ExternalIntegration,
  allIntegrations: ExternalIntegration[] | undefined,
): boolean => {
  if (!allIntegrations) {
    return false
  }
  const integrationsForProvider = allIntegrations.filter((i) => i.provider === integration.provider)
  return integrationsForProvider.length === 1
}
